import { NoticeDoNot } from '@fs/zion-icon'
import { Button, Checkbox, DialogOverlay, DialogOverlayActions, Paragraph, TextField, useOverlay } from '@fs/zion-ui'
import PropTypes from 'prop-types'
import React, { useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { restrictAllPatronArtifacts } from '../../../services/artifactService'
import useNotifications from '../../../hooks/useNotifications'
import { PermissionsContext } from '../../app/PermissionsContext'

/**
 * Button shown to Data Admin 6 on the patron artifacts page to
 * restrict all of a patron’s artifacts. Manages its own dialog,
 * performs the action, and calls onRestrict callback.
 */
const RestrictAllPatronArtifactsButton = ({
  patronId,
  patronUserName,
  totalArtifactsSpanningPages,
  refetch,
  disabled,
}) => {
  const [t] = useTranslation()
  const { isAuthorized } = useContext(PermissionsContext)
  const { error } = useNotifications()
  const [idConfirmation, setIdConfirmation] = useState('')
  const [sendEmail, setSendEmail] = useState(true)
  const confirmDialogOpener = useOverlay()
  const focusElementRef = useRef()

  const confirmRestrict = async () => {
    restrictAllPatronArtifacts(patronId, sendEmail)
      .then(() => {
        confirmDialogOpener.close()
        refetch()
      })
      .catch((err) => {
        error(t('artifacts.error.errorRestrictingPatronArtifacts', 'Error restricting all patron artifacts'), err)
        confirmDialogOpener.close()
        refetch()
      })
  }

  if (!isAuthorized('ArtPrmCanBatchRestrict')) {
    return null
  }

  return (
    <>
      <Button Icon={NoticeDoNot} emphasis="medium" onClick={confirmDialogOpener.handleClick} disabled={disabled}>
        {`${t('artifacts.restrictAll', 'Restrict All')} (${totalArtifactsSpanningPages})`}
      </Button>
      <DialogOverlay
        focusRef={focusElementRef}
        title={t('artifacts.restrictAllArtifacts', 'Restrict All Artifacts')}
        {...confirmDialogOpener}
      >
        <Paragraph size="sm">
          {`${t(
            'artifacts.restrictAllConfirmation',
            'Are you sure you want to restrict all currently unrestricted artifacts uploaded by'
          )} ${patronUserName}?`}
        </Paragraph>
        <Paragraph size="sm">
          {`${t('artifacts.restrictAllEnterPatronId', 'Please type in this patron’s ID to confirm.')} (${patronId})`}
        </Paragraph>
        <TextField
          ref={focusElementRef}
          id="restrictAllPatronIdInput"
          label={t('artifacts.patronId', 'Patron ID')}
          value={idConfirmation}
          onChange={(e) => setIdConfirmation(e.target.value)}
          type="text"
          fullwidth="true"
        />
        <Checkbox
          name="NotifyPatronViaEmailCheckbox"
          label={t('artifacts.restrictAllNotifyPatron', 'Notify patron via email')}
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
        />
        <DialogOverlayActions>
          <Button onClick={confirmRestrict} emphasis="high" disabled={Number(idConfirmation) !== patronId}>
            {t('action.submit', 'Submit')}
          </Button>
          <Button onClick={confirmDialogOpener.close}>{t('action.cancel', 'Cancel')}</Button>
        </DialogOverlayActions>
      </DialogOverlay>
    </>
  )
}

RestrictAllPatronArtifactsButton.propTypes = {
  totalArtifactsSpanningPages: PropTypes.number.isRequired,
  patronId: PropTypes.number.isRequired,
  patronUserName: PropTypes.string.isRequired,
  refetch: PropTypes.func,
  disabled: PropTypes.bool.isRequired,
}

export default RestrictAllPatronArtifactsButton
